import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Star, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { publicExpertApi, clientPortalApi, getErrorMessage } from '../../lib/http';
import type {
  ExpertPageProfile,
  ExpertService,
  AvailabilityDay,
  AvailabilityWeek,
  AvailabilityMonth,
} from '../../types';
import { ServiceList } from '../../components/expert/ServiceList';
import { CalendarModeToggle } from '../../components/expert/CalendarModeToggle';
import { CalendarNavigator } from '../../components/expert/CalendarNavigator';
import { CalendarDay, CalendarWeek, CalendarMonth } from '../../components/expert/CalendarViews';
import PublicBookingModal from '../../components/public/PublicBookingModal';

type CalendarMode = 'day' | 'week' | 'month';

const PublicExpertPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // Data state
  const [profile, setProfile] = useState<ExpertPageProfile | null>(null);
  const [services, setServices] = useState<ExpertService[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [availability, setAvailability] = useState<
    AvailabilityDay | AvailabilityWeek | AvailabilityMonth | null
  >(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Client profile for prefilling booking modal
  const [clientProfile, setClientProfile] = useState<{ name?: string; email?: string; phone?: string } | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load expert profile and services on mount
  useEffect(() => {
    if (!slug) return;

    const loadExpertData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [profileRes, servicesRes] = await Promise.all([
          publicExpertApi.getProfile(slug),
          publicExpertApi.getServices(slug, { active_only: true }),
        ]);

        setProfile(profileRes.data);
        setServices(servicesRes.data.services);

        // Auto-select if single service
        if (servicesRes.data.services.length === 1) {
          setSelectedServiceId(servicesRes.data.services[0].id);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    loadExpertData();
  }, [slug]);

  // Try to fetch logged-in client profile for prefilling booking form
  useEffect(() => {
    clientPortalApi.profile.get()
      .then(res => {
        const { name, email, phone } = res.data;
        setClientProfile({ name, email, phone });
      })
      .catch(() => {
        // Silently ignore — user is not logged in as client
      });
  }, []);

  // Load availability when service, mode, or anchor date changes
  useEffect(() => {
    if (!slug || !selectedServiceId) {
      setAvailability(null);
      return;
    }

    const loadAvailability = async () => {
      try {
        setAvailabilityLoading(true);

        const dateStr = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}-${String(anchorDate.getDate()).padStart(2, '0')}`;

        const res = await publicExpertApi.getAvailability(slug, {
          service_id: selectedServiceId,
          mode: calendarMode,
          date: dateStr,
        });

        setAvailability(res.data);
      } catch (err) {
        console.error('Error loading availability:', err);
      } finally {
        setAvailabilityLoading(false);
      }
    };

    loadAvailability();
  }, [slug, selectedServiceId, calendarMode, anchorDate]);

  const handleServiceSelect = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setSelectedSlot(null);
    setCalendarMode('month');
  };

  const handleModeChange = (mode: CalendarMode) => {
    setCalendarMode(mode);
    setSelectedSlot(null);
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(anchorDate);

    if (calendarMode === 'day') {
      newDate.setDate(anchorDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (calendarMode === 'week') {
      newDate.setDate(anchorDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (calendarMode === 'month') {
      newDate.setMonth(anchorDate.getMonth() + (direction === 'next' ? 1 : -1));
    }

    setAnchorDate(newDate);
    setSelectedSlot(null);
  };

  const handleDateClick = (date: string) => {
    setAnchorDate(new Date(date));
    setCalendarMode('day');
    setSelectedSlot(null);
  };

  const handleSlotClick = (slot: string) => {
    setSelectedSlot(slot);
    setIsModalOpen(true);
  };

  const handleBookingSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSlot(null);
    toast.success('Booking created successfully! Check your email for confirmation.');

    // Refresh availability
    if (slug && selectedServiceId) {
      const dateStr = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}-${String(anchorDate.getDate()).padStart(2, '0')}`;
      publicExpertApi.getAvailability(slug, {
        service_id: selectedServiceId,
        mode: calendarMode,
        date: dateStr,
      }).then(res => setAvailability(res.data));
    }
  }, [slug, selectedServiceId, calendarMode, anchorDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!profile || error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-8 rounded-lg text-center">
            <h2 className="text-xl font-semibold mb-2">Expert not found</h2>
            <p className="text-red-600 mb-4">{error || 'The expert you are looking for does not exist or has been removed.'}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800"
            >
              <ArrowLeft className="w-4 h-4" />
              Go to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedService = services.find((s) => s.id === selectedServiceId);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 max-w-5xl">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
              {profile.display_name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{profile.display_name}</h1>
              {profile.location && (profile.location.city || profile.location.country) && (
                <p className="text-gray-500 flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />
                  {[profile.location.city, profile.location.country].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
            {profile.rating > 0 && (
              <div className="flex items-center gap-1 text-amber-500">
                <Star className="w-5 h-5 fill-current" />
                <span className="font-semibold">{profile.rating.toFixed(1)}</span>
              </div>
            )}
          </div>
          {profile.bio && (
            <p className="mt-4 text-gray-600">{profile.bio}</p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Services List */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Services
            </h2>
            <ServiceList
              services={services}
              selectedServiceId={selectedServiceId}
              onSelectService={handleServiceSelect}
            />
          </div>

          {/* Calendar & Booking */}
          <div className="lg:col-span-2">
            {!selectedServiceId ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Select a service to view available times</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {/* Calendar Controls */}
                <div className="flex items-center justify-center mb-6">
                  <CalendarModeToggle mode={calendarMode} onChange={handleModeChange} />
                </div>

                <CalendarNavigator
                  mode={calendarMode}
                  anchorDate={anchorDate}
                  onNavigate={handleNavigate}
                />

                {/* Calendar Content */}
                {availabilityLoading ? (
                  <div className="py-12 text-center">
                    <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto" />
                    <p className="text-gray-500 mt-4">Loading availability...</p>
                  </div>
                ) : availability ? (
                  <div className="mt-6">
                    {availability.mode === 'day' && (
                      <CalendarDay
                        data={availability as AvailabilityDay}
                        selectedSlot={selectedSlot}
                        onSlotClick={handleSlotClick}
                      />
                    )}
                    {availability.mode === 'week' && (
                      <CalendarWeek
                        data={availability as AvailabilityWeek}
                        selectedSlot={selectedSlot}
                        onSlotClick={handleSlotClick}
                      />
                    )}
                    {availability.mode === 'month' && (
                      <CalendarMonth
                        data={availability as AvailabilityMonth}
                        onDateClick={handleDateClick}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-8">
        <div className="container mx-auto px-4 py-4 max-w-5xl">
          <p className="text-center text-gray-500 text-sm">
            Powered by <span className="text-purple-600 font-medium">myTimeSlot</span>
          </p>
        </div>
      </div>

      {/* Booking Modal */}
      {selectedService && selectedSlot && profile && (
        <PublicBookingModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          expertSlug={slug!}
          expertName={profile.display_name}
          service={selectedService}
          slotStart={selectedSlot}
          onSuccess={handleBookingSuccess}
          initialData={clientProfile || undefined}
          timezone={profile.timezone}
        />
      )}
    </div>
  );
};

export default PublicExpertPage;
